// frontend/src/components/shop/CelebrationOverlay.js
// Shop V1 — "Wizard's Fanciful Magic Show" celebration overlay.
// Pure CSS @keyframes animations — no animation libraries.
// No audio — deferred to V2 per approved decisions.

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui';
import { equipVariant, FALLBACK_PLAYER_IMAGE } from '../../api/shopApi';
import { resolveMediaUrl } from '../../api/transport';
import './CelebrationOverlay.css';

// ---------------------------------------------------------------------------
// CardImage — two-stage image fallback
//
// Stage 1 (primary):   image_url from backend (opaque gateway URL)
// Stage 2 (fallback):  ${PUBLIC_URL}/art/variants/prototype_set/${gsis_id}.png
// Stage 3 (final):     FALLBACK_PLAYER_IMAGE (chest/mystery image)
//
// State tracks which fallback level is active to avoid infinite onError loops.
// ---------------------------------------------------------------------------
const FALLBACK_STAGE_PRIMARY  = 0;
const FALLBACK_STAGE_LOCAL    = 1;
const FALLBACK_STAGE_CHEST    = 2;

function CardImage({ imageUrl, gsisId, playerName }) {
    const [fallbackStage, setFallbackStage] = useState(FALLBACK_STAGE_PRIMARY);

    // Reset fallback stage whenever the card's imageUrl changes (new outcome)
    useEffect(() => {
        setFallbackStage(FALLBACK_STAGE_PRIMARY);
    }, [imageUrl]);

    // Fix 4: local prototype fallback is dev-only — never expose prototype_set paths in production
    const isDev = process.env.NODE_ENV === 'development';
    const localFallbackUrl = isDev
        ? `${process.env.PUBLIC_URL}/art/variants/prototype_set/${gsisId}.png`
        : null;

    let src;
    if (fallbackStage === FALLBACK_STAGE_PRIMARY) {
        // resolveMediaUrl converts the relative /api/... gateway path to an absolute
        // backend URL in dev (React dev server and Django run on different ports).
        src = (imageUrl ? resolveMediaUrl(imageUrl) : null) || (isDev ? localFallbackUrl : FALLBACK_PLAYER_IMAGE);
    } else if (fallbackStage === FALLBACK_STAGE_LOCAL && isDev && localFallbackUrl) {
        src = localFallbackUrl;
    } else {
        src = FALLBACK_PLAYER_IMAGE;
    }

    const handleError = useCallback(() => {
        setFallbackStage(prev => {
            if (prev < FALLBACK_STAGE_CHEST) return prev + 1;
            return prev; // already at final fallback — do not advance further
        });
    }, []);

    return (
        <img
            src={src}
            alt={playerName}
            className="celebration-card-image"
            onError={handleError}
        />
    );
}

// Staggered animation delays for up to 5 cards (ms)
const CARD_REVEAL_DELAYS = [0, 120, 240, 360, 480];

// Number of sparkle particles to render
const SPARKLE_COUNT = 10;

/**
 * Generate sparkle particle data (left position + animation delay).
 * Positions are spread across 10%–90% of the overlay width.
 */
function generateSparkles() {
    return Array.from({ length: SPARKLE_COUNT }, (_, i) => ({
        id: i,
        left: `${10 + (i * 8)}%`,
        delay: `${(i * 100) % 1000}ms`,
    }));
}

const SPARKLES = generateSparkles();

/**
 * CelebrationOverlay
 *
 * Fullscreen overlay triggered after openShopOffer() resolves.
 * Cards reveal one at a time with staggered animation.
 * Tap anywhere = skip animation.
 *
 * @param {object}   props
 * @param {object}   props.outcome          — outcome from openShopOffer()
 * @param {Function} props.onClose          — called on skip or dismiss
 * @param {boolean}  props.lightweight      — true for 2nd+ consecutive single pulls
 */
function CelebrationOverlay({ outcome, onClose, lightweight }) {
    const [equipStatus, setEquipStatus] = useState({}); // { [ownership_id]: 'idle'|'loading'|'equipped'|'error' }
    const [equipErrors, setEquipErrors] = useState({});  // { [ownership_id]: errorMessage }
    const [equippedImageUrls, setEquippedImageUrls] = useState({}); // { [ownership_id]: image_url }

    // Reset equip status when outcome changes
    useEffect(() => {
        if (!outcome) return;
        const initial = {};
        // PR6 receipt shape: cards live under outcome.receipt.cards.
        const outcomeCards = (outcome.receipt && outcome.receipt.cards) ? outcome.receipt.cards : (outcome.cards || []);
        outcomeCards.forEach(card => {
            initial[card.ownership_id] = 'idle';
        });
        setEquipStatus(initial);
        setEquipErrors({});
        setEquippedImageUrls({});
    }, [outcome]);

    const handleEquip = useCallback(async (card) => {
        if (!card.ownership_id) {
            throw new Error(
                `CelebrationOverlay [handleEquip]: card.ownership_id is required — cannot equip without it. Card: ${JSON.stringify(card)}`
            );
        }
        setEquipStatus(prev => ({ ...prev, [card.ownership_id]: 'loading' }));
        try {
            const response = await equipVariant(card.ownership_id, card.gsis_id);
            // response.equipped may be an object (live) or true (mock) — handle both
            const equippedImageUrl = (response?.equipped && typeof response.equipped === 'object')
                ? response.equipped.image_url
                : null;
            if (equippedImageUrl) {
                setEquippedImageUrls(prev => ({ ...prev, [card.ownership_id]: equippedImageUrl }));
            }
            setEquipStatus(prev => ({ ...prev, [card.ownership_id]: 'equipped' }));
        } catch (err) {
            setEquipStatus(prev => ({ ...prev, [card.ownership_id]: 'error' }));
            setEquipErrors(prev => ({ ...prev, [card.ownership_id]: err.message }));
        }
    }, []);

    if (!outcome) return null;

    // PR6 receipt shape: cards live under outcome.receipt.cards.
    // Fall back to outcome.cards for backward compatibility during transition.
    const cards = (outcome.receipt && outcome.receipt.cards) ? outcome.receipt.cards : (outcome.cards || []);
    const overlayClass = [
        'celebration-overlay',
        lightweight ? 'celebration-overlay--lightweight' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={overlayClass} role="dialog" aria-modal="true" aria-label="Card reveal">
            {/* Sparkle particles — hidden in lightweight mode via CSS */}
            <div className="celebration-sparkles" aria-hidden="true">
                {SPARKLES.map(s => (
                    <span
                        key={s.id}
                        className="sparkle"
                        style={{ left: s.left, animationDelay: s.delay }}
                    />
                ))}
            </div>

            {/* Main content panel */}
            <div className="celebration-panel" onClick={e => e.stopPropagation()}>
                <h2 className="celebration-headline">
                    {cards.length === 1 ? "✨ You Got It! ✨" : "✨ Pack Opened! ✨"}
                </h2>

                {/* Punch card reward notice */}
                {outcome.freeGranted && (
                    <div className="celebration-free-granted">
                        🎉 Punch card complete! You earned a free pull!
                    </div>
                )}

                {/* Card reveal grid */}
                <div className="celebration-cards">
                    {cards.map((card, idx) => {
                        const status = equipStatus[card.ownership_id] || 'idle';
                        const errMsg = equipErrors[card.ownership_id];
                        const delay = CARD_REVEAL_DELAYS[Math.min(idx, CARD_REVEAL_DELAYS.length - 1)];

                        return (
                            <div
                                key={card.ownership_id}
                                className={`celebration-card${card.is_duplicate ? ' celebration-card--duplicate' : ''}`}
                                style={{ animationDelay: `${delay}ms` }}
                            >
                                {/* Player image — two-stage fallback via CardImage */}
                                <div className="celebration-card-image-wrap">
                                    <CardImage
                                        imageUrl={equippedImageUrls[card.ownership_id] || card.image_url}
                                        gsisId={card.gsis_id}
                                        playerName={card.player_name_snapshot}
                                    />
                                    {card.is_duplicate && (
                                        <span className="celebration-duplicate-badge">DUPLICATE</span>
                                    )}
                                </div>

                                {/* Card info */}
                                <div className="celebration-card-info">
                                    <span className="celebration-card-name">{card.player_name_snapshot}</span>
                                    <span className="celebration-card-meta">
                                            {card.position}{card.team_name_snapshot ? ` - ${card.team_name_snapshot}` : ''}
                                        </span>
                                    <span className="celebration-card-theme">{card.theme_key}</span>
                                </div>

                                {/* Equip button */}
                                <div className="celebration-card-actions">
                                    {status === 'equipped' ? (
                                        <span className="celebration-equipped-label">✓ Equipped!</span>
                                    ) : status === 'error' ? (
                                        <span className="celebration-equip-error" title={errMsg}>
                                            ⚠ Equip failed
                                        </span>
                                    ) : (
                                        <Button
                                            variant="primary"
                                            onClick={() => handleEquip(card)}
                                            disabled={status === 'loading'}
                                        >
                                            {status === 'loading' ? 'Equipping...' : 'Equip Now'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Full-overlay skip zone — tap anywhere outside panel to close */}
            <div className="celebration-skip-zone" onClick={onClose} aria-label="Tap to skip">
                <span className="celebration-skip-label">Tap anywhere to skip</span>
            </div>
        </div>
    );
}

export default CelebrationOverlay;
